import { supabase } from '../lib/supabase';


/**
 * HR Service - Handles all employee and HR-related operations
 * Database schema uses snake_case, React uses camelCase
 */

// Cache for employee data to reduce API calls
let employeeCache = null;
let employeeCacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const hrService = {
  /**
   * Clear employee cache
   */
  clearEmployeeCache() {
    employeeCache = null;
    employeeCacheTimestamp = null;
    console.log('Employee cache cleared');
  },

  /**
   * Get all employees with their user profile data
   */
  async getAllEmployees() {
    // Check cache first
    if (employeeCache && employeeCacheTimestamp && (Date.now() - employeeCacheTimestamp < CACHE_DURATION)) {
      console.log('Returning cached employee data');
      return employeeCache;
    }

    const { data, error } = await supabase?.from('employees')?.select(`
        id,
        employee_id,
        user_id,
        position,
        department,
        designation,
        branch_name,
        work_shift,
        salary,
        hourly_rate,
        employment_status,
        hire_date,
        date_of_joining,
        role_assigned,
        reporting_manager_id,
        supervisor_id,
        created_at,
        updated_at,
        user_profiles!employees_user_id_fkey (
          id,
          full_name,
          email,
          phone,
          username,
          avatar_url,
          role,
          address,
          city,
          state,
          zip,
          date_of_birth,
          gender,
          marital_status,
          nationality,
          religion,
          blood_group,
          emergency_contact_name,
          emergency_contact_phone,
          emergency_contact_relationship,
          emergency_contact_email,
          pf_number,
          esi_number,
          bank_name,
          account_number,
          ifsc_code,
          education_qualification,
          professional_experience
        )
      `)?.order('employee_id');

    if (error) throw error;

    // Convert snake_case to camelCase
    const employees = data?.map(emp => ({
      id: emp?.id,
      employeeId: emp?.employee_id,
      userId: emp?.user_id,
      fullName: emp?.user_profiles?.full_name || 'Unknown',
      name: emp?.user_profiles?.full_name || 'Unknown',
      email: emp?.user_profiles?.email || '',
      phone: emp?.user_profiles?.phone || '',
      username: emp?.user_profiles?.username || '',
      position: emp?.position,
      department: emp?.department,
      designation: emp?.designation,
      branchName: emp?.branch_name,
      workShift: emp?.work_shift,
      salary: parseFloat(emp?.salary || 0),
      hourlyRate: parseFloat(emp?.hourly_rate || 0),
      employmentStatus: emp?.employment_status,
      hireDate: emp?.hire_date,
      joinDate: emp?.hire_date || emp?.date_of_joining,
      dateOfJoining: emp?.hire_date || emp?.date_of_joining,
      roleAssigned: emp?.role_assigned,
      reportingManagerId: emp?.reporting_manager_id,
      supervisorId: emp?.supervisor_id,
      avatarUrl: emp?.user_profiles?.avatar_url,
      role: emp?.user_profiles?.role,
      createdAt: emp?.created_at,
      updatedAt: emp?.updated_at,
      userProfiles: emp?.user_profiles,
      personalDetails: {
        address: {
          street: emp?.user_profiles?.address || '',
          city: emp?.user_profiles?.city || '',
          state: emp?.user_profiles?.state || '',
          zipCode: emp?.user_profiles?.zip || ''
        },
        dateOfBirth: emp?.user_profiles?.date_of_birth,
        gender: emp?.user_profiles?.gender,
        maritalStatus: emp?.user_profiles?.marital_status,
        nationality: emp?.user_profiles?.nationality,
        religion: emp?.user_profiles?.religion,
        bloodGroup: emp?.user_profiles?.blood_group,
        emergencyContact: {
          name: emp?.user_profiles?.emergency_contact_name,
          phone: emp?.user_profiles?.emergency_contact_phone,
          relationship: emp?.user_profiles?.emergency_contact_relationship,
          email: emp?.user_profiles?.emergency_contact_email
        },
        pfNumber: emp?.user_profiles?.pf_number,
        esiNumber: emp?.user_profiles?.esi_number,
        bankName: emp?.user_profiles?.bank_name,
        accountNumber: emp?.user_profiles?.account_number,
        ifscCode: emp?.user_profiles?.ifsc_code,
        educationQualification: emp?.user_profiles?.education_qualification,
        professionalExperience: emp?.user_profiles?.professional_experience
      }
    }));

    // Update cache
    employeeCache = employees;
    employeeCacheTimestamp = Date.now();

    return employees;
  },

  /**
   * Get employees who can be reporting managers
   * Returns active employees with manager, team lead, or senior roles
   */
  async getReportingManagers() {
    try {
      const { data, error } = await supabase?.from('employees')?.select(`
        id,
        employee_id,
        user_id,
        position,
        department,
        employment_status,
        user_profiles!employees_user_id_fkey (
          id,
          full_name,
          email
        )
      `)?.eq('employment_status', 'active')?.order('employee_id');

      if (error) throw error;

      // Return all active employees who can potentially be managers
      return data || [];
    } catch (error) {
      console.error('Error fetching reporting managers:', error);
      throw error;
    }
  },

  /**
   * Get employees filtered by status
   */
  async getAllEmployeesByStatus({ status = 'active', showInactiveOnly = false }) {
    let query = supabase?.from('employees')?.select(`
        id,
        employee_id,
        user_id,
        position,
        department,
        designation,
        branch_name,
        work_shift,
        salary,
        hourly_rate,
        employment_status,
        hire_date,
        date_of_joining,
        role_assigned,
        reporting_manager_id,
        supervisor_id,
        created_at,
        updated_at,
        user_profiles!employees_user_id_fkey (
          id,
          full_name,
          email,
          phone,
          username,
          avatar_url,
          role
        )
      `);

    if (showInactiveOnly) {
      query = query?.in('employment_status', ['terminated', 'resigned', 'inactive']);
    } else if (status) {
      query = query?.eq('employment_status', status);
    }

    const { data, error } = await query?.order('employee_id');

    if (error) throw error;

    return data?.map(emp => ({
      id: emp?.id,
      employeeId: emp?.employee_id,
      userId: emp?.user_id,
      fullName: emp?.user_profiles?.full_name || 'Unknown',
      name: emp?.user_profiles?.full_name || 'Unknown',
      email: emp?.user_profiles?.email || '',
      phone: emp?.user_profiles?.phone || '',
      username: emp?.user_profiles?.username || '',
      position: emp?.position,
      department: emp?.department,
      designation: emp?.designation,
      branchName: emp?.branch_name,
      workShift: emp?.work_shift,
      salary: parseFloat(emp?.salary || 0),
      hourlyRate: parseFloat(emp?.hourly_rate || 0),
      employmentStatus: emp?.employment_status,
      hireDate: emp?.hire_date,
      joinDate: emp?.hire_date || emp?.date_of_joining,
      dateOfJoining: emp?.hire_date || emp?.date_of_joining,
      roleAssigned: emp?.role_assigned,
      reportingManagerId: emp?.reporting_manager_id,
      supervisorId: emp?.supervisor_id,
      avatarUrl: emp?.user_profiles?.avatar_url,
      role: emp?.user_profiles?.role,
      userProfiles: emp?.user_profiles
    }));
  },

  /**
   * Get employee by ID
   */
  async getEmployeeById(employeeId) {
    const { data, error } = await supabase?.from('employees')?.select(`
        id,
        employee_id,
        user_id,
        position,
        department,
        designation,
        branch_name,
        work_shift,
        salary,
        hourly_rate,
        employment_status,
        hire_date,
        date_of_joining,
        role_assigned,
        reporting_manager_id,
        supervisor_id,
        created_at,
        updated_at,
        user_profiles!employees_user_id_fkey (
          id,
          full_name,
          email,
          phone,
          username,
          avatar_url,
          role,
          address,
          city,
          state,
          zip,
          date_of_birth,
          gender,
          marital_status,
          nationality,
          religion,
          blood_group,
          emergency_contact_name,
          emergency_contact_phone,
          emergency_contact_relationship,
          emergency_contact_email,
          pf_number,
          esi_number,
          bank_name,
          account_number,
          ifsc_code,
          education_qualification,
          professional_experience
        )
      `)?.eq('id', employeeId)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      employeeId: data?.employee_id,
      userId: data?.user_id,
      fullName: data?.user_profiles?.full_name || '',
      name: data?.user_profiles?.full_name || '',
      email: data?.user_profiles?.email || '',
      phone: data?.user_profiles?.phone || '',
      username: data?.user_profiles?.username || '',
      position: data?.position,
      department: data?.department,
      designation: data?.designation,
      branchName: data?.branch_name,
      workShift: data?.work_shift,
      salary: parseFloat(data?.salary || 0),
      hourlyRate: parseFloat(data?.hourly_rate || 0),
      employmentStatus: data?.employment_status,
      hireDate: data?.hire_date,
      joinDate: data?.hire_date || data?.date_of_joining,
      dateOfJoining: data?.hire_date || data?.date_of_joining,
      roleAssigned: data?.role_assigned,
      reportingManagerId: data?.reporting_manager_id,
      reportingManager: data?.reporting_manager_id,
      supervisorId: data?.supervisor_id,
      avatarUrl: data?.user_profiles?.avatar_url,
      role: data?.user_profiles?.role,
      personalDetails: {
        address: {
          street: data?.user_profiles?.address || '',
          city: data?.user_profiles?.city || '',
          state: data?.user_profiles?.state || '',
          zipCode: data?.user_profiles?.zip || ''
        },
        dateOfBirth: data?.user_profiles?.date_of_birth,
        gender: data?.user_profiles?.gender,
        maritalStatus: data?.user_profiles?.marital_status,
        nationality: data?.user_profiles?.nationality,
        religion: data?.user_profiles?.religion,
        bloodGroup: data?.user_profiles?.blood_group,
        emergencyContact: {
          name: data?.user_profiles?.emergency_contact_name,
          phone: data?.user_profiles?.emergency_contact_phone,
          relationship: data?.user_profiles?.emergency_contact_relationship,
          email: data?.user_profiles?.emergency_contact_email
        },
        emergencyContactName: data?.user_profiles?.emergency_contact_name,
        emergencyContactPhone: data?.user_profiles?.emergency_contact_phone,
        emergencyContactRelationship: data?.user_profiles?.emergency_contact_relationship,
        emergencyContactEmail: data?.user_profiles?.emergency_contact_email,
        pfNumber: data?.user_profiles?.pf_number,
        esiNumber: data?.user_profiles?.esi_number,
        bankName: data?.user_profiles?.bank_name,
        accountNumber: data?.user_profiles?.account_number,
        ifscCode: data?.user_profiles?.ifsc_code,
        educationQualification: data?.user_profiles?.education_qualification,
        professionalExperience: data?.user_profiles?.professional_experience
      }
    };
  },

  /**
   * Create new employee
   */
  async createEmployee(employeeData) {
    const { data, error } = await supabase?.rpc('create_employee', {
      p_employee_id: employeeData?.employeeId,
      p_position: employeeData?.position,
      p_department: employeeData?.department,
      p_hire_date: employeeData?.hireDate,
      p_salary: employeeData?.salary,
      p_hourly_rate: employeeData?.hourlyRate || 0,
      p_employment_status: employeeData?.employmentStatus || 'active',
      p_designation: employeeData?.designation,
      p_branch_name: employeeData?.branchName,
      p_work_shift: employeeData?.workShift || 'day',
      p_role_assigned: employeeData?.roleAssigned,
      p_reporting_manager_id: employeeData?.reportingManagerId,
      p_supervisor_id: employeeData?.supervisorId,
      p_full_name: employeeData?.userProfileData?.fullName,
      p_email: employeeData?.userProfileData?.email,
      p_phone: employeeData?.userProfileData?.phone,
      p_address: employeeData?.userProfileData?.address,
      p_city: employeeData?.userProfileData?.city,
      p_state: employeeData?.userProfileData?.state,
      p_zip: employeeData?.userProfileData?.zip,
      p_role: employeeData?.userProfileData?.role || 'employee',
      p_username: employeeData?.username,
      p_password: employeeData?.password || 'TempPass123!',
      p_date_of_birth: employeeData?.personalDetails?.dateOfBirth,
      p_gender: employeeData?.personalDetails?.gender,
      p_marital_status: employeeData?.personalDetails?.maritalStatus,
      p_nationality: employeeData?.personalDetails?.nationality,
      p_religion: employeeData?.personalDetails?.religion,
      p_blood_group: employeeData?.personalDetails?.bloodGroup,
      p_emergency_contact_name: employeeData?.personalDetails?.emergencyContact?.name || employeeData?.personalDetails?.emergencyContactName,
      p_emergency_contact_phone: employeeData?.personalDetails?.emergencyContact?.phone || employeeData?.personalDetails?.emergencyContactPhone,
      p_emergency_contact_relationship: employeeData?.personalDetails?.emergencyContact?.relationship || employeeData?.personalDetails?.emergencyContactRelationship,
      p_emergency_contact_email: employeeData?.personalDetails?.emergencyContact?.email || employeeData?.personalDetails?.emergencyContactEmail,
      p_pf_number: employeeData?.personalDetails?.pfNumber,
      p_esi_number: employeeData?.personalDetails?.esiNumber,
      p_bank_name: employeeData?.personalDetails?.bankName,
      p_account_number: employeeData?.personalDetails?.accountNumber,
      p_ifsc_code: employeeData?.personalDetails?.ifscCode,
      p_education_qualification: employeeData?.personalDetails?.educationQualification,
      p_professional_experience: employeeData?.personalDetails?.professionalExperience
    });

    if (error) throw error;

    // Clear cache after creating employee
    this.clearEmployeeCache();

    return { data };
  },

  /**
   * Update employee
   */
  async updateEmployee(employeeId, updateData) {
    // Update employees table
    const employeeUpdate = {
      position: updateData?.position,
      department: updateData?.department,
      designation: updateData?.designation,
      branch_name: updateData?.branchName,
      work_shift: updateData?.workShift,
      salary: updateData?.salary,
      hourly_rate: updateData?.hourlyRate,
      employment_status: updateData?.employmentStatus,
      // CRITICAL FIX: Store date as plain string without timezone conversion
      hire_date: updateData?.hireDate || null,
      date_of_joining: updateData?.hireDate || null,
      role_assigned: updateData?.roleAssigned,
      reporting_manager_id: updateData?.reportingManagerId,
      supervisor_id: updateData?.supervisorId,
      updated_at: new Date()?.toISOString()
    };

    const { error: empError } = await supabase?.from('employees')?.update(employeeUpdate)?.eq('id', employeeId);

    if (empError) throw empError;

    // Get user_id for updating user_profiles
    const { data: employee } = await supabase?.from('employees')?.select('user_id')?.eq('id', employeeId)?.single();

    if (employee?.user_id) {
      // Update user_profiles table
      const profileUpdate = {
        full_name: updateData?.name,
        email: updateData?.email,
        phone: updateData?.phone,
        username: updateData?.username,
        address: updateData?.personalDetails?.address?.street,
        city: updateData?.personalDetails?.address?.city,
        state: updateData?.personalDetails?.address?.state,
        zip: updateData?.personalDetails?.address?.zipCode,
        date_of_birth: updateData?.personalDetails?.dateOfBirth,
        gender: updateData?.personalDetails?.gender,
        marital_status: updateData?.personalDetails?.maritalStatus,
        nationality: updateData?.personalDetails?.nationality,
        religion: updateData?.personalDetails?.religion,
        blood_group: updateData?.personalDetails?.bloodGroup,
        emergency_contact_name: updateData?.personalDetails?.emergencyContact?.name || updateData?.personalDetails?.emergencyContactName,
        emergency_contact_phone: updateData?.personalDetails?.emergencyContact?.phone || updateData?.personalDetails?.emergencyContactPhone,
        emergency_contact_relationship: updateData?.personalDetails?.emergencyContact?.relationship || updateData?.personalDetails?.emergencyContactRelationship,
        emergency_contact_email: updateData?.personalDetails?.emergencyContact?.email || updateData?.personalDetails?.emergencyContactEmail,
        pf_number: updateData?.personalDetails?.pfNumber,
        esi_number: updateData?.personalDetails?.esiNumber,
        bank_name: updateData?.personalDetails?.bankName,
        account_number: updateData?.personalDetails?.accountNumber,
        ifsc_code: updateData?.personalDetails?.ifscCode,
        education_qualification: updateData?.personalDetails?.educationQualification,
        professional_experience: updateData?.personalDetails?.professionalExperience,
        updated_at: new Date()?.toISOString()
      };

      const { error: profileError } = await supabase?.from('user_profiles')?.update(profileUpdate)?.eq('id', employee?.user_id);

      if (profileError) throw profileError;
    }

    // Clear cache after updating employee
    this.clearEmployeeCache();

    return { success: true };
  },

  /**
   * Delete employee (hard delete)
   */
  async deleteEmployee(employeeId) {
    const { error } = await supabase?.from('employees')?.delete()?.eq('id', employeeId);

    if (error) throw error;

    // Clear cache after deleting employee
    this.clearEmployeeCache();

    return { success: true };
  },

  /**
   * Update employee password (Admin only)
   * Uses the database function reset_employee_password
   */
  async updateEmployeePassword(userId, newPassword) {
    if (!userId || !newPassword) {
      throw new Error('User ID and new password are required');
    }

    if (newPassword?.length < 6) {
      throw new Error('Password must be at least 6 characters long');
    }

    // Call the database function to reset password
    const { data, error } = await supabase?.rpc('reset_employee_password', {
      employee_user_id: userId,
      new_password: newPassword
    });

    if (error) {
      console.error('Error updating employee password:', error);
      throw new Error(error.message || 'Failed to update employee password');
    }

    return { success: true, data };
  },

  /**
   * Get attendance records
   */
  async getAttendanceRecords({ employeeId = null, startDate = null, endDate = null, limit = null } = {}) {
    let query = supabase?.from('attendance_records')?.select(`
        id,
        employee_id,
        attendance_date,
        check_in_time,
        check_out_time,
        total_hours,
        attendance_status,
        notes,
        created_at,
        employees!attendance_records_employee_id_fkey (
          employee_id,
          user_profiles!employees_user_id_fkey (
            full_name
          )
        )
      `);

    if (employeeId) {
      query = query?.eq('employee_id', employeeId);
    }

    if (startDate) {
      query = query?.gte('attendance_date', startDate);
    }

    if (endDate) {
      query = query?.lte('attendance_date', endDate);
    }

    if (limit) {
      query = query?.limit(limit);
    }

    const { data, error } = await query?.order('attendance_date', { ascending: false });

    if (error) throw error;

    return data?.map(record => ({
      id: record?.id,
      employeeId: record?.employee_id,
      employeeCode: record?.employees?.employee_id,
      employeeName: record?.employees?.user_profiles?.full_name || 'Unknown',
      attendanceDate: record?.attendance_date,
      checkInTime: record?.check_in_time,
      checkOutTime: record?.check_out_time,
      totalHours: parseFloat(record?.total_hours || 0),
      attendanceStatus: record?.attendance_status,
      status: record?.attendance_status,
      notes: record?.notes,
      createdAt: record?.created_at
    }));
  },

  /**
   * Parse CSV attendance data (auto-detect format)
   */
  async parseCSVAttendanceAuto(csvContent) {
    const { data, error } = await supabase?.rpc('parse_matrix_csv_attendance', {
      csv_content: csvContent
    });

    if (error) throw error;

    // Get all unique employee codes from parsed data
    const employeeCodes = [...new Set(data?.map(record => record?.employee_id)?.filter(Boolean))];

    // Lookup employee UUIDs based on employee codes
    const { data: employees, error: empError } = await supabase
      ?.from('employees')
      ?.select('id, employee_id')
      ?.in('employee_id', employeeCodes);

    if (empError) throw empError;

    // Create a map of employee_code -> employee_uuid
    const employeeMap = new Map();
    employees?.forEach(emp => {
      employeeMap?.set(emp?.employee_id, emp?.id);
    });

    // Transform parsed data to camelCase and map employee codes to UUIDs
    const transformedRecords = data
      ?.filter(record => !record?.parse_error) // Filter out error records
      ?.map(record => {
        const employeeUuid = employeeMap?.get(record?.employee_id);
        if (!employeeUuid) {
          console.warn(`Employee code ${record?.employee_id} not found in database`);
          return null;
        }
        return {
          employeeId: employeeUuid,
          attendanceDate: record?.attendance_date,
          attendanceStatus: record?.attendance_status,
          totalHours: parseFloat(record?.working_hours || 0),
          notes: record?.notes,
          checkInTime: null,
          checkOutTime: null
        };
      })
      ?.filter(Boolean); // Remove null entries

    return {
      records: transformedRecords || [],
      totalRecords: transformedRecords?.length || 0
    };
  },

  /**
   * Upload attendance records
   */
  async uploadAttendanceRecords(records) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Convert records to database format
    const dbRecords = records?.map(record => ({
      employee_id: record?.employeeId,
      attendance_date: record?.attendanceDate,
      check_in_time: record?.checkInTime,
      check_out_time: record?.checkOutTime,
      total_hours: record?.totalHours,
      attendance_status: record?.attendanceStatus,
      notes: record?.notes,
      created_by: user?.id
    }));

    const { data, error } = await supabase?.from('attendance_records')?.upsert(dbRecords, {
        onConflict: 'employee_id,attendance_date',
        ignoreDuplicates: false
      });

    if (error) throw error;

    return {
      success: true,
      totalRecords: records?.length,
      successful: records?.length,
      failed: 0,
      recordsUploaded: records?.length
    };
  },

  /**
   * Get leave requests
   */
  async getLeaveRequests({ employeeId = null, status = null } = {}) {
    let query = supabase?.from('leave_requests')?.select(`
        id,
        employee_id,
        leave_type,
        start_date,
        end_date,
        total_days,
        reason,
        leave_status,
        approved_by,
        approved_at,
        comments,
        created_at,
        employees!leave_requests_employee_id_fkey (
          employee_id,
          user_profiles!employees_user_id_fkey (
            full_name,
            email
          )
        )
      `);

    if (employeeId) {
      query = query?.eq('employee_id', employeeId);
    }

    if (status) {
      query = query?.eq('leave_status', status);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(request => ({
      id: request?.id,
      employeeId: request?.employee_id,
      employeeCode: request?.employees?.employee_id,
      employeeName: request?.employees?.user_profiles?.full_name || 'Unknown',
      employeeEmail: request?.employees?.user_profiles?.email || '',
      leaveType: request?.leave_type,
      startDate: request?.start_date,
      endDate: request?.end_date,
      totalDays: request?.total_days,
      reason: request?.reason,
      leaveStatus: request?.leave_status,
      status: request?.leave_status,
      approvedBy: request?.approved_by,
      approvedAt: request?.approved_at,
      comments: request?.comments,
      createdAt: request?.created_at
    }));
  },

  /**
   * Get pending leave requests
   */
  async getPendingLeaveRequests() {
    return this.getLeaveRequests({ status: 'pending' });
  },

  /**
   * Create leave request
   */
  async createLeaveRequest(leaveData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get employee ID for current user
    const { data: employee } = await supabase?.from('employees')?.select('id')?.eq('user_id', user?.id)?.single();

    if (!employee) throw new Error('Employee record not found');

    const dbData = {
      employee_id: employee?.id,
      leave_type: leaveData?.leaveType,
      start_date: leaveData?.startDate,
      end_date: leaveData?.endDate,
      total_days: leaveData?.totalDays,
      reason: leaveData?.reason,
      leave_status: 'pending'
    };

    const { data, error } = await supabase?.from('leave_requests')?.insert(dbData)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      leaveType: data?.leave_type,
      startDate: data?.start_date,
      endDate: data?.end_date,
      totalDays: data?.total_days,
      reason: data?.reason,
      leaveStatus: data?.leave_status
    };
  },

  /**
   * Approve/reject leave request
   */
  async approveLeaveRequest(requestId, approved, comments = '') {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const updateData = {
      leave_status: approved ? 'approved' : 'rejected',
      approved_by: user?.id,
      approved_at: new Date()?.toISOString(),
      comments: comments
    };

    const { data, error } = await supabase?.from('leave_requests')?.update(updateData)?.eq('id', requestId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      leaveStatus: data?.leave_status
    };
  },

  /**
   * Get leave balances
   */
  async getLeaveBalances() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get employee ID for current user
    const { data: employee, error: empError } = await supabase?.from('employees')?.select('id')?.eq('user_id', user?.id)?.single();

    if (empError || !employee) {
      return {
        error: 'NO_EMPLOYEE_RECORD',
        message: 'Employee record not found. Please contact HR.'
      };
    }

    const { data, error } = await supabase?.from('leave_balances')?.select('*')?.eq('employee_id', employee?.id)?.order('year', { ascending: false })?.order('month', { ascending: false });

    if (error) throw error;

    return data?.map(balance => ({
      id: balance?.id,
      employeeId: balance?.employee_id,
      year: balance?.year,
      month: balance?.month,
      openingBalance: parseFloat(balance?.opening_balance || 0),
      earnedBalance: parseFloat(balance?.earned_balance || 0),
      usedBalance: parseFloat(balance?.used_balance || 0),
      closingBalance: parseFloat(balance?.closing_balance || 0),
      isEligibleForAccrual: balance?.is_eligible_for_accrual,
      attendanceDays: balance?.attendance_days,
      leaveDaysTaken: balance?.leave_days_taken
    }));
  },

  /**
   * Get current leave balance summary
   */
  async getCurrentLeaveBalanceSummary() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get employee ID for current user
    const { data: employee } = await supabase?.from('employees')?.select('id')?.eq('user_id', user?.id)?.single();

    if (!employee) throw new Error('Employee record not found');

    const { data, error } = await supabase?.from('leave_balances')?.select('closing_balance')?.eq('employee_id', employee?.id)?.order('year', { ascending: false })?.order('month', { ascending: false })?.limit(1)?.single();

    if (error && error?.code !== 'PGRST116') throw error;

    return {
      currentBalance: parseFloat(data?.closing_balance || 0)
    };
  },

  /**
   * Process leave balance for employee
   */
  async processLeaveBalanceForEmployee(employeeId, year, month) {
    const { data, error } = await supabase?.rpc('process_leave_balance', {
      p_employee_id: employeeId,
      p_year: year,
      p_month: month
    });

    if (error) throw error;

    return { success: true, data };
  },

  /**
   * Process monthly leave balance for all employees
   */
  async processMonthlyLeaveBalance(year = null, month = null) {
    const currentDate = new Date();
    const targetYear = year || currentDate?.getFullYear();
    const targetMonth = month || currentDate?.getMonth() + 1;

    const { data, error } = await supabase?.rpc('process_monthly_leave_balance', {
      p_year: targetYear,
      p_month: targetMonth
    });

    if (error) throw error;

    return {
      processedCount: data?.length || 0,
      results: data
    };
  },

  /**
   * Get monthly targets
   */
  async getMonthlyTargets({ targetMonth = null } = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get employee ID for current user
    const { data: employee } = await supabase?.from('employees')?.select('id')?.eq('user_id', user?.id)?.single();

    if (!employee) throw new Error('Employee record not found');

    let query = supabase?.from('monthly_targets')?.select('*')?.eq('employee_id', employee?.id);

    if (targetMonth) {
      query = query?.eq('target_month', targetMonth);
    }

    const { data, error } = await query?.order('due_date', { ascending: true });

    if (error) throw error;

    return data?.map(target => ({
      id: target?.id,
      employeeId: target?.employee_id,
      targetMonth: target?.target_month,
      targetTitle: target?.target_title,
      targetDescription: target?.target_description,
      targetValue: parseFloat(target?.target_value || 0),
      targetUnit: target?.target_unit,
      achievedValue: parseFloat(target?.achieved_value || 0),
      achievementPercentage: parseFloat(target?.achievement_percentage || 0),
      targetStatus: target?.target_status,
      startDate: target?.start_date,
      dueDate: target?.due_date,
      notes: target?.notes
    }));
  },

  /**
   * Get targets summary
   */
  async getTargetsSummary({ targetMonth = null } = {}) {
    const targets = await this.getMonthlyTargets({ targetMonth });

    const totalTargets = targets?.length;
    const completedTargets = targets?.filter(t => t?.targetStatus === 'completed')?.length;
    const inProgressTargets = targets?.filter(t => t?.targetStatus === 'in_progress')?.length;
    const overdueTargets = targets?.filter(t => t?.targetStatus === 'overdue')?.length;
    const averageAchievement = totalTargets > 0
      ? targets?.reduce((sum, t) => sum + t?.achievementPercentage, 0) / totalTargets
      : 0;

    return {
      totalTargets,
      completedTargets,
      inProgressTargets,
      overdueTargets,
      averageAchievement: Math.round(averageAchievement)
    };
  },

  /**
   * Create monthly target
   */
  async createMonthlyTarget(targetData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbData = {
      employee_id: targetData?.employeeId,
      target_month: targetData?.targetMonth,
      target_title: targetData?.targetTitle,
      target_description: targetData?.targetDescription,
      target_value: targetData?.targetValue,
      target_unit: targetData?.targetUnit || 'units',
      due_date: targetData?.dueDate,
      created_by: user?.id
    };

    const { data, error } = await supabase?.from('monthly_targets')?.insert(dbData)?.select()?.single();

    if (error) throw error;

    return { success: true, data };
  },

  /**
   * Update target achievement
   */
  async updateTargetAchievement(targetId, achievedValue, notes = '') {
    const updateData = {
      achieved_value: achievedValue,
      notes: notes,
      updated_at: new Date()?.toISOString()
    };

    const { data, error } = await supabase?.from('monthly_targets')?.update(updateData)?.eq('id', targetId)?.select()?.single();

    if (error) throw error;

    return { success: true, data };
  },

  /**
   * Get admin dashboard metrics
   */
  async getAdminDashboardMetrics() {
    const { data, error } = await supabase?.rpc('get_admin_dashboard_metrics');

    if (error) throw error;

    const metrics = data?.[0] || {};

    return {
      totalEmployees: parseInt(metrics?.total_employees || 0),
      activeEmployees: parseInt(metrics?.active_employees || 0),
      pendingLeaveRequests: parseInt(metrics?.pending_leave_requests || 0),
      pendingPayroll: parseInt(metrics?.pending_payroll || 0)
    };
  },

  /**
   * Get admin actions log
   */
  async getAdminActionsLog({ limit = 10 } = {}) {
    const { data, error } = await supabase?.from('admin_actions_log')?.select(`
        id,
        action_type,
        action_data,
        created_at,
        user_profiles!admin_actions_log_admin_id_fkey (
          full_name
        )
      `)?.order('created_at', { ascending: false })?.limit(limit);

    if (error) throw error;

    return data?.map(action => ({
      id: action?.id,
      actionType: action?.action_type,
      actionData: action?.action_data,
      adminName: action?.user_profiles?.full_name || 'Unknown',
      createdAt: action?.created_at
    }));
  },

  /**
   * Get pending payroll items
   */
  async getPendingPayrollItems() {
    const { data, error } = await supabase?.from('salary_records')?.select(`
        id,
        employee_id,
        base_salary,
        gross_pay,
        net_pay,
        payroll_status,
        pay_period_start,
        pay_period_end,
        employees!salary_records_employee_id_fkey (
          employee_id,
          user_profiles!employees_user_id_fkey (
            full_name
          )
        )
      `)?.eq('payroll_status', 'draft')?.order('pay_period_start', { ascending: false });

    if (error) throw error;

    return data?.map(item => ({
      id: item?.id,
      employeeId: item?.employee_id,
      employeeCode: item?.employees?.employee_id,
      employeeName: item?.employees?.user_profiles?.full_name || 'Unknown',
      baseSalary: parseFloat(item?.base_salary || 0),
      grossPay: parseFloat(item?.gross_pay || 0),
      netPay: parseFloat(item?.net_pay || 0),
      payrollStatus: item?.payroll_status,
      payPeriodStart: item?.pay_period_start,
      payPeriodEnd: item?.pay_period_end
    }));
  },

  /**
   * Create payroll record
   */
  async createPayrollRecord(payrollData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbData = {
      employee_id: payrollData?.employeeId,
      base_salary: payrollData?.baseSalary,
      allowances: payrollData?.allowances || 0,
      bonuses: payrollData?.bonuses || 0,
      incentives: payrollData?.incentives || 0,
      overtime_hours: payrollData?.overtimeHours || 0,
      overtime_pay: payrollData?.overtimePay || 0,
      deductions: payrollData?.deductions || 0,
      tax_deductions: payrollData?.taxDeductions || 0,
      pay_period_start: payrollData?.payPeriodStart,
      pay_period_end: payrollData?.payPeriodEnd,
      payroll_notes: payrollData?.payrollNotes || '',
      processed_by: user?.id
    };

    const { data, error } = await supabase?.from('salary_records')?.insert(dbData)?.select()?.single();

    if (error) throw error;

    return { success: true, data };
  },

  /**
   * Get payroll records
   */
  async getPayrollRecords(filters = {}) {
    let query = supabase?.from('salary_records')?.select(`
        id,
        employee_id,
        base_salary,
        allowances,
        bonuses,
        incentives,
        overtime_pay,
        gross_pay,
        deductions,
        tax_deductions,
        net_pay,
        payroll_status,
        pay_period_start,
        pay_period_end,
        processed_at,
        employees!salary_records_employee_id_fkey (
          employee_id,
          user_profiles!employees_user_id_fkey (
            full_name
          )
        )
      `);

    if (filters?.year) {
      const startDate = `${filters?.year}-01-01`;
      const endDate = `${filters?.year}-12-31`;
      query = query?.gte('pay_period_start', startDate)?.lte('pay_period_end', endDate);
    }

    if (filters?.month) {
      const monthStr = filters?.month?.toString()?.padStart(2, '0');
      query = query?.ilike('pay_period_start', `%-${monthStr}-%`);
    }

    if (filters?.employeeId) {
      query = query?.eq('employee_id', filters?.employeeId);
    }

    const { data, error } = await query?.order('pay_period_start', { ascending: false });

    if (error) throw error;

    return data?.map(record => ({
      id: record?.id,
      employeeId: record?.employee_id,
      employeeCode: record?.employees?.employee_id,
      employeeName: record?.employees?.user_profiles?.full_name || 'Unknown',
      baseSalary: parseFloat(record?.base_salary || 0),
      allowances: parseFloat(record?.allowances || 0),
      bonuses: parseFloat(record?.bonuses || 0),
      incentives: parseFloat(record?.incentives || 0),
      overtimePay: parseFloat(record?.overtime_pay || 0),
      grossPay: parseFloat(record?.gross_pay || 0),
      deductions: parseFloat(record?.deductions || 0),
      taxDeductions: parseFloat(record?.tax_deductions || 0),
      netPay: parseFloat(record?.net_pay || 0),
      payrollStatus: record?.payroll_status,
      payPeriodStart: record?.pay_period_start,
      payPeriodEnd: record?.pay_period_end,
      processedAt: record?.processed_at
    }));
  }
};

export default hrService;
